import { client } from "@vellum/vellum-api/client.gen";
import {
  getMyFeatureFlags,
  suggestIcons,
  type SuggestIconsRequest,
} from "@vellum/vellum-api";
import { VELLUM_API_ORIGINS } from "@vellum/vellum-api/origins";
// Imported from the `@vellum/ui/fetch` subpath rather than the root barrel:
// that barrel imports this package back (AppHeader -> useOptionalAuth), and the
// `setConfig` below runs at module scope, where a cycle through the barrel could
// hand us an uninitialized binding.
import { createRetryingFetch } from "@vellum/ui/fetch";

import { resolveEnvironment } from "./environment";

/** Resolve the Vellum API origin for the current environment. */
export function getVellumApiOrigin(): string {
  return VELLUM_API_ORIGINS[resolveEnvironment()];
}

/** Google SSO entrypoint on the Vellum API host (not the app's own SPA host). */
export function getGoogleLoginUrl(): string {
  return `${getVellumApiOrigin()}/api/v1/auth/generate-callback`;
}

// Point the shared @vellum/vellum-api client at the vellum-api origin and
// include the session cookie on every request. This runs when the module
// loads, so importing `vellumApiSdk` below guarantees a configured client.
client.setConfig({
  baseUrl: getVellumApiOrigin(),
  credentials: "include",
  // Every micro app gets the same treatment for a dropped connection: retry an
  // idempotent request with backoff, then surface a connection-error body
  // rather than a rejected promise (PEAK-456). These apps have no error
  // reporter wired up, so failures are left to the caller's error state.
  fetch: createRetryingFetch(),
});

export * as vellumApiSdk from "@vellum/vellum-api";

/**
 * Fetch the caller's evaluated feature flags as a flat `{ key: bool }` map.
 * Used as the `fetchFlags` fetcher for `FeatureFlagProvider`. On an
 * unauthenticated or failed request the provider falls back to no flags
 * (fail closed), so callers don't need to handle errors here.
 */
export async function fetchFeatureFlags(): Promise<Record<string, boolean>> {
  const result = await getMyFeatureFlags();
  return result.data ?? {};
}

/**
 * Fetch AI-suggested icon names, best first. Used as the `onSuggest` fetcher
 * for `AIIconPicker`, which hides its suggestions when this throws.
 */
export async function suggestIconNames(
  request: SuggestIconsRequest,
): Promise<string[]> {
  const result = await suggestIcons({ body: request });
  if (result.data === undefined) {
    throw new Error("Icon suggestion request failed");
  }
  return result.data.icons;
}
